import { useMutation } from '@tanstack/react-query';
import { apiClient } from '@copro/api';
import { enqueueSnackbar } from '../../../components/GlobalSnackbar/snackbarStore';

type Variables = {
  email: string;
  currentPassword: string;
  newPassword: string;
};

export type ChangePasswordFailure = 'wrong-current-password' | 'same-password' | 'weak-password' | 'unknown';

export class ChangePasswordError extends Error {
  readonly reason: ChangePasswordFailure;

  constructor(reason: ChangePasswordFailure, message: string) {
    super(message);
    this.reason = reason;
  }
}

function toFailure(code: string | undefined): ChangePasswordFailure {
  if (code === 'same_password') return 'same-password';
  if (code === 'weak_password') return 'weak-password';
  return 'unknown';
}

async function changePassword(variables: Variables) {
  // Checking the current password first keeps an unattended session from changing it.
  const { error: signInError } = await apiClient.auth.signInWithPassword({
    email: variables.email,
    password: variables.currentPassword,
  });

  if (signInError) {
    throw new ChangePasswordError(
      signInError.code === 'invalid_credentials' ? 'wrong-current-password' : 'unknown',
      signInError.message,
    );
  }

  const { error } = await apiClient.auth.updateUser({ password: variables.newPassword });

  if (error) {
    throw new ChangePasswordError(toFailure(error.code), error.message);
  }
}

export default function useChangePassword() {
  return useMutation({
    mutationFn: changePassword,
    onSuccess: () => {
      enqueueSnackbar('Mot de passe modifié.', 'success');
    },
  });
}
